import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import HeroSection from '../../components/feature/HeroSection';
import TrustBar from '../../components/feature/TrustBar';
import ServicesSection from '../../components/feature/ServicesSection';
import AIAnswerBlock from '../../components/feature/AIAnswerBlock';
import ProcessSection from '../../components/feature/ProcessSection';
import WhyChooseUs from '../../components/feature/WhyChooseUs';
import ServiceAreasSection from '../../components/feature/ServiceAreasSection';
import TestimonialsSection from '../../components/feature/TestimonialsSection';
import FAQSection from '../../components/feature/FAQSection';
import FinalCTA from '../../components/feature/FinalCTA';
import RevealOnScroll from '../../components/base/RevealOnScroll';
import { serviceAreas } from '../../mocks/serviceAreas';
import { locationsFaqs } from '../../mocks/faqs';

const heroContent = {
  badge: 'Serving Greater Houston TX',
  headline: 'Houston-Area Home Improvement, at Your Door.',
  subheadline: 'Your Neighborhood. Our Expertise.',
  description: 'One Call Handyman Service serves Houston TX and all surrounding communities — professional property solutions delivered right to your neighborhood with 16+ years of local excellence.',
  primaryCTA: { text: 'Get Free Quote', href: '#quote' },
  secondaryCTA: { text: 'Call 346-363-8363', href: 'tel:3463638363' },
  backgroundImage: 'https://public.readdy.ai/ai/img_res/767b6d3c441cc4fb4baf05e683a7a46d.jpg',
  stats: [
    { value: '20+', label: 'Houston Communities' },
    { value: '16+', label: 'Years Local' },
    { value: '5.0 ★', label: 'Google Rated' },
    { value: '7 Days', label: 'Available' },
  ],
};

const aiContent = {
  eyebrow: 'Local Coverage',
  title: 'Serving Houston TX & Surrounding Areas',
  lead: 'One Call Handyman Service provides professional home improvement and handyman services across Greater Houston TX — covering Houston\'s primary communities and every surrounding neighborhood within the metro area.',
  points: [
    {
      icon: 'ri-map-pin-fill',
      title: 'Houston, TX (Primary)',
      body: 'Based in Houston TX 77075, One Call Handyman Service is deeply embedded in the local community. We serve all Houston zip codes with make-ready remodeling, flooring, drywall, electrical, plumbing, and roofing services.',
    },
    {
      icon: 'ri-compass-line',
      title: 'South Houston Area',
      body: 'Serving Pearland, Friendswood, League City, Galveston, La Porte, and Deer Park with the same professional standard as our Houston clients. South Houston homeowners receive full-service property solutions.',
    },
    {
      icon: 'ri-arrow-left-right-line',
      title: 'West Houston Area',
      body: 'Sugar Land, Missouri City, Stafford, Richmond, Katy, and Cypress — all served by One Call Handyman Service. West Houston homeowners get 16+ years of expertise delivered directly to their neighborhood.',
    },
    {
      icon: 'ri-arrow-right-line',
      title: 'East Houston Area',
      body: 'Pasadena, La Porte, Deer Park, and Baytown TX are fully covered by our team. East Houston residents receive the same professional, transparent service that has made us Greater Houston\'s most trusted name.',
    },
    {
      icon: 'ri-arrow-up-line',
      title: 'North Houston Area',
      body: 'Spring, Cypress, and The Woodlands TX — One Call Handyman brings Houston-quality property solutions to north Houston communities. Same professional standard, same satisfaction guarantee.',
    },
    {
      icon: 'ri-ship-line',
      title: 'Galveston Bay Area',
      body: 'Serving Clear Lake, Webster, Seabrook, and Kemah TX near Galveston Bay. Coastal Houston-area homeowners benefit from our full range of professional handyman and home improvement services.',
    },
  ],
};

const cityGroups = [
  {
    region: 'Houston Core',
    cities: ['Houston, TX'],
    primary: true,
  },
  {
    region: 'South Houston',
    cities: ['Pearland', 'Friendswood', 'League City', 'Galveston', 'La Porte', 'Deer Park'],
  },
  {
    region: 'West Houston',
    cities: ['Sugar Land', 'Missouri City', 'Stafford', 'Richmond', 'Katy'],
  },
  {
    region: 'Northwest Houston',
    cities: ['Cypress', 'Spring', 'The Woodlands'],
  },
  {
    region: 'East Houston',
    cities: ['Pasadena', 'Baytown'],
  },
  {
    region: 'Bay Area',
    cities: ['Clear Lake', 'Webster', 'Seabrook', 'Kemah'],
  },
];

export default function LocationsPage() {
  return (
    <main className="pb-16 lg:pb-0">
      <Navbar />
      <HeroSection content={heroContent} />
      <TrustBar />

      <section className="bg-white py-28 lg:py-36">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <RevealOnScroll className="mb-14">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-px bg-[#BF9448]" />
              <span className="text-[#BF9448] text-[10px] font-sans font-semibold tracking-[0.25em] uppercase">
                Coverage Map
              </span>
            </div>
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5">
              <h2
                className="font-serif font-semibold text-[#0F0F0F] leading-tight"
                style={{ fontSize: 'clamp(2rem, 3.5vw, 3rem)' }}
              >
                Greater Houston Service Area
              </h2>
              <p className="font-sans text-[#7A7265] max-w-lg text-base leading-relaxed lg:pb-1">
                Professional property solutions wherever you call home in the Greater Houston metro area.
              </p>
            </div>
          </RevealOnScroll>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-14">
            {cityGroups.map((group, i) => (
              <RevealOnScroll key={i} delayMs={i * 80}>
                <div className={`border p-6 ${group.primary ? 'bg-[#BF9448] border-[#BF9448]' : 'bg-[#F8F4EF] border-[#E8E2D9]'}`}>
                  <div className="flex items-center gap-2 mb-4">
                    <span className="w-5 h-5 flex items-center justify-center">
                      <i className={`ri-map-pin-line text-base ${group.primary ? 'text-white' : 'text-[#BF9448]'}`}></i>
                    </span>
                    <h3 className={`font-sans text-xs font-semibold uppercase tracking-wider ${group.primary ? 'text-white' : 'text-[#0F0F0F]'}`}>
                      {group.region}
                    </h3>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {group.cities.map((city) => (
                      <span
                        key={city}
                        className={`text-xs font-sans px-3 py-1.5 ${
                          group.primary
                            ? 'bg-white/20 text-white font-semibold'
                            : 'bg-white text-[#4A4440] border border-[#E0D9CF]'
                        }`}
                      >
                        {city}
                      </span>
                    ))}
                  </div>
                </div>
              </RevealOnScroll>
            ))}
          </div>

          <RevealOnScroll>
            <div className="relative overflow-hidden border border-[#DDD5C8]" style={{ height: '420px' }}>
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d443461.5347653148!2d-95.82299855!3d29.7201865!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8640b8b4488d8501%3A0xca0d02def365053b!2sHouston%2C%20TX!5e0!3m2!1sen!2sus!4v1700000000000!5m2!1sen!2sus"
                width="100%"
                height="420"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="One Call Handyman Service Houston TX service area coverage map"
              />
              <div className="absolute top-4 left-4 bg-[#0F0F0F] px-4 py-3">
                <div className="font-sans text-white text-xs font-semibold tracking-wide">Greater Houston Service Area</div>
                <div className="font-sans text-white/50 text-[10px] mt-0.5">Houston TX &amp; 20+ Surrounding Cities</div>
              </div>
            </div>
          </RevealOnScroll>
        </div>
      </section>

      <AIAnswerBlock content={aiContent} />
      <ProcessSection />
      <WhyChooseUs
        image="https://readdy.ai/api/search-image?query=professional%20skilled%20home%20improvement%20craftsman%20working%20luxury%20residential%20renovation%20Houston%20Texas%20upscale%20interior%20expert%20precision%20tools%20quality%20workmanship%20warm%20natural%20light%20professional%20portrait%20vertical&width=1090&height=990&seq=locations-why-001&orientation=portrait"
        title="Local Expertise Delivered to Your Door"
        subtitle="We don't just work in Houston — we know Houston. Every neighborhood, every building type, every local code. That local knowledge shows in every project we complete."
      />
      <ServiceAreasSection
        title="Every Community in Greater Houston"
        subtitle="Professional handyman and home remodeling services available in Houston TX and all 20+ surrounding cities."
      />
      <TestimonialsSection />
      <FAQSection
        faqs={locationsFaqs}
        title="Service Area Questions — Answered"
        subtitle="What Houston-area homeowners ask about our geographic coverage and local service availability."
        bg="cream"
      />
      <ServicesSection
        title="All Services Available Across Greater Houston"
        subtitle="Every professional service available to Houston homeowners is also available throughout our full service area."
        bg="white"
      />
      <FinalCTA
        headline="We Serve Your Houston-Area Neighborhood"
        subheadline="Request your free quote today. We confirm your service area immediately and schedule your project fast."
      />
      <Footer />
    </main>
  );
}
